import { useEffect, useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Search, Eye, RefreshCw, Archive } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { TableSortHeader, useTableSort } from "./TableSortHeader";
import { StatusFilter } from "./StatusFilter";

interface ContactSubmission {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  subject: string | null;
  message: string;
  created_at: string;
  read_at: string | null;
  archived: boolean;
}

const STATUS_OPTIONS = [
  { value: "all", label: "All messages" },
  { value: "unread", label: "Unread" },
  { value: "read", label: "Read" },
  { value: "archived", label: "Archived" },
];

export const AdminContactsTab = () => {
  const [contacts, setContacts] = useState<ContactSubmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedContact, setSelectedContact] = useState<ContactSubmission | null>(null);
  const { toast } = useToast();
  const { sortKey, sortDirection, handleSort, sortData } = useTableSort<ContactSubmission>("created_at", "desc");

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('contact_submissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setContacts(data || []);
    } catch (error) {
      toast({
        title: "Error loading data",
        description: "Failed to load contact submissions. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const markAsRead = async (contact: ContactSubmission) => {
    try {
      await supabase
        .from('contact_submissions')
        .update({ read_at: new Date().toISOString() })
        .eq('id', contact.id);

      setContacts(prev => prev.map(c => 
        c.id === contact.id ? { ...c, read_at: new Date().toISOString() } : c
      ));
      setSelectedContact({ ...contact, read_at: new Date().toISOString() });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to mark as read.",
        variant: "destructive",
      });
    }
  };

  const toggleArchive = async (contact: ContactSubmission) => {
    try {
      await supabase
        .from('contact_submissions')
        .update({ archived: !contact.archived })
        .eq('id', contact.id);

      setContacts(prev => prev.map(c => 
        c.id === contact.id ? { ...c, archived: !c.archived } : c
      ));
      toast({
        title: contact.archived ? "Unarchived" : "Archived",
        description: `Message ${contact.archived ? "restored" : "archived"} successfully.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update archive status.",
        variant: "destructive",
      });
    }
  };

  const viewContact = (contact: ContactSubmission) => {
    setSelectedContact(contact);
    if (!contact.read_at) {
      markAsRead(contact);
    }
  };

  const filteredContacts = useMemo(() => {
    let result = contacts;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(contact =>
        contact.name.toLowerCase().includes(searchLower) ||
        contact.email.toLowerCase().includes(searchLower) ||
        contact.message.toLowerCase().includes(searchLower) ||
        contact.subject?.toLowerCase().includes(searchLower)
      );
    }

    if (statusFilter !== "all") {
      result = result.filter(contact => {
        if (statusFilter === "archived") return contact.archived;
        if (statusFilter === "unread") return !contact.read_at && !contact.archived;
        if (statusFilter === "read") return contact.read_at && !contact.archived;
        return true;
      });
    }

    return result;
  }, [contacts, searchTerm, statusFilter]);

  const sortedContacts = useMemo(() => sortData(filteredContacts), [filteredContacts, sortKey, sortDirection]);

  const unreadCount = contacts.filter(c => !c.read_at && !c.archived).length;
  const archivedCount = contacts.filter(c => c.archived).length;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>Contact Submissions</CardTitle>
              <CardDescription>
                {unreadCount} unread, {archivedCount} archived
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search messages..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-48"
                />
              </div>
              <StatusFilter
                value={statusFilter}
                onValueChange={setStatusFilter}
                options={STATUS_OPTIONS}
              />
              <Button variant="outline" size="icon" onClick={fetchData}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : sortedContacts.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              {searchTerm || statusFilter !== "all" ? "No results match your filters" : "No contact submissions yet"}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <TableSortHeader
                        label="Name"
                        sortKey="name"
                        currentSortKey={sortKey}
                        currentDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </TableHead>
                    <TableHead>
                      <TableSortHeader
                        label="Email"
                        sortKey="email"
                        currentSortKey={sortKey}
                        currentDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </TableHead>
                    <TableHead>
                      <TableSortHeader
                        label="Subject"
                        sortKey="subject"
                        currentSortKey={sortKey}
                        currentDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>
                      <TableSortHeader
                        label="Date"
                        sortKey="created_at"
                        currentSortKey={sortKey}
                        currentDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedContacts.map((contact) => (
                    <TableRow 
                      key={contact.id} 
                      className={!contact.read_at && !contact.archived ? "bg-primary/5" : ""}
                    >
                      <TableCell className="font-medium">{contact.name}</TableCell>
                      <TableCell>{contact.email}</TableCell>
                      <TableCell>{contact.subject || "General enquiry"}</TableCell>
                      <TableCell>
                        {contact.archived ? (
                          <Badge variant="outline">Archived</Badge>
                        ) : contact.read_at ? (
                          <Badge variant="secondary">Read</Badge>
                        ) : (
                          <Badge>New</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {format(new Date(contact.created_at), "dd MMM yyyy HH:mm")}
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button variant="ghost" size="sm" onClick={() => viewContact(contact)}>
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => toggleArchive(contact)}>
                          <Archive className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selectedContact} onOpenChange={() => setSelectedContact(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Contact Message</DialogTitle>
            <DialogDescription>
              From {selectedContact?.name} on {selectedContact && format(new Date(selectedContact.created_at), "dd MMMM yyyy 'at' HH:mm")}
            </DialogDescription>
          </DialogHeader>
          {selectedContact && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-muted-foreground">Email</span>
                  <p>{selectedContact.email}</p>
                </div>
                {selectedContact.phone && (
                  <div>
                    <span className="font-medium text-muted-foreground">Phone</span>
                    <p>{selectedContact.phone}</p>
                  </div>
                )}
                {selectedContact.subject && (
                  <div className="col-span-2">
                    <span className="font-medium text-muted-foreground">Subject</span>
                    <p>{selectedContact.subject}</p>
                  </div>
                )}
              </div>
              <div>
                <span className="font-medium text-muted-foreground text-sm">Message</span>
                <div className="mt-2 p-4 bg-muted/50 rounded-lg whitespace-pre-wrap text-sm">
                  {selectedContact.message}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" size="sm" onClick={() => toggleArchive(selectedContact)}>
                  <Archive className="h-4 w-4 mr-1" />
                  {selectedContact.archived ? "Unarchive" : "Archive"}
                </Button>
                <Button asChild size="sm">
                  <a href={`mailto:${selectedContact.email}`}>
                    Reply via Email
                  </a>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
